<script lang="ts">
  // A compact kebab (⋯) overflow menu for table-row actions. The menu is PORTALED
  // to <body> and positioned with fixed coords from the trigger's rect, so the
  // Card's `overflow-hidden` (rounded corners) can't clip it. Closes on outside
  // click, Escape, scroll, or resize.
  export type ActionItem = {
    label: string;
    onSelect: () => void;
    tone?: "default" | "danger";
    disabled?: boolean;
  };

  let {
    items,
    label = "More actions",
    disabled = false,
  }: {
    items: ActionItem[];
    label?: string;
    disabled?: boolean;
  } = $props();

  let open = $state(false);
  let placed = $state(false);
  let trigger = $state<HTMLButtonElement | null>(null);
  let menu = $state<HTMLDivElement | null>(null);
  let top = $state(0);
  let left = $state(0);

  // Reparent the menu to <body> for the lifetime of the {#if} block.
  function portal(node: HTMLElement) {
    document.body.appendChild(node);
    return { destroy: () => node.remove() };
  }

  function place() {
    if (!trigger || !menu) return;
    const r = trigger.getBoundingClientRect();
    const mw = menu.offsetWidth || 180;
    const mh = menu.offsetHeight || 0;
    left = Math.max(8, r.right - mw); // right-align the menu to the trigger
    top = r.bottom + 6 + mh > window.innerHeight ? r.top - mh - 6 : r.bottom + 6; // flip up near the edge
    placed = true;
  }

  function toggle() {
    if (disabled) return;
    open = !open;
    placed = false;
  }
  function close() {
    open = false;
    placed = false;
  }
  function pick(it: ActionItem) {
    if (it.disabled) return;
    close();
    it.onSelect();
  }

  // Measure + position once the menu has mounted (hidden until then to avoid a flash).
  $effect(() => {
    if (open && menu) place();
  });

  function onKeydown(e: KeyboardEvent) {
    if (open && e.key === "Escape") {
      close();
      trigger?.focus();
    }
  }
  function onDocClick(e: MouseEvent) {
    if (!open) return;
    const t = e.target as Node;
    if (trigger?.contains(t) || menu?.contains(t)) return;
    close();
  }
</script>

<svelte:window onkeydown={onKeydown} onresize={close} onscroll={close} />
<svelte:document onclick={onDocClick} />

<button
  bind:this={trigger}
  type="button"
  {disabled}
  aria-label={label}
  aria-haspopup="menu"
  aria-expanded={open}
  onclick={toggle}
  class="inline-flex items-center justify-center size-8 rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-ink disabled:opacity-55 disabled:pointer-events-none"
>
  <span class="flex flex-col items-center gap-[3px]" aria-hidden="true">
    <span class="size-[3px] rounded-full bg-current"></span>
    <span class="size-[3px] rounded-full bg-current"></span>
    <span class="size-[3px] rounded-full bg-current"></span>
  </span>
</button>

{#if open}
  <div use:portal>
    <div
      bind:this={menu}
      role="menu"
      tabindex="-1"
      style="position:fixed; top:{top}px; left:{left}px; visibility:{placed ? 'visible' : 'hidden'};"
      class="z-50 min-w-[180px] rounded-md border border-border bg-surface py-1 shadow-lg"
    >
      {#each items as it (it.label)}
        <button
          type="button"
          role="menuitem"
          disabled={it.disabled}
          onclick={() => pick(it)}
          class="block w-full px-3.5 py-2 text-left text-sm transition-colors disabled:opacity-55 disabled:pointer-events-none
            {it.tone === 'danger'
            ? 'text-danger hover:bg-danger-bg'
            : 'text-ink hover:bg-surface-muted'}"
        >
          {it.label}
        </button>
      {/each}
    </div>
  </div>
{/if}
